const normalizeLine = (value: string) => String(value || "").replace(/\s+/g, " ").trim();
const stripEdgeMarkdown = (value: string) =>
  String(value || "")
    .replace(/^\*\*+/, "")
    .replace(/\*\*+$/, "")
    .trim();

const formatSubjectDetails = (rows: Array<[string, unknown]>) =>
  rows
    .map(([label, value]) => {
      const normalizedValue = normalizeLine(String(value || ""));
      return normalizedValue ? `${label}: ${normalizedValue}` : "";
    })
    .filter(Boolean)
    .join("; ");

export const extractProfileRowsFromSections = (sections: any[] = []) =>
  (Array.isArray(sections) ? sections : [])
    .flatMap((section) =>
      String(section?.content_markdown || section?.content || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
    )
    .map((line) => {
      const match = line.match(/^([^:]+):\s*(.+)$/);
      if (!match) return null;
      return {
        label: normalizeLine(stripEdgeMarkdown(match[1])),
        value: normalizeLine(stripEdgeMarkdown(match[2])),
      };
    })
    .filter(Boolean) as Array<{ label: string; value: string }>;

export const buildVictimProfileImagePrompt = ({
  title = "",
  sections = [],
}: {
  title?: string;
  sections?: any[];
}) => {
  const rows = extractProfileRowsFromSections(sections);
  const subjectTitle = normalizeLine(title) || "Unknown victim";
  const descriptors = rows
    .map((row) => `${row.label}: ${row.value}`)
    .filter(Boolean)
    .join("; ");

  if (!descriptors) {
    return `Photorealistic investigative victim dossier portrait for ${subjectTitle}. Documentary realism, natural skin texture, neutral background, case-file composition, no text overlays.`;
  }

  return `Photorealistic investigative victim dossier portrait for ${subjectTitle}. Subject details: ${descriptors}. Documentary realism, natural skin texture, neutral background, evidence-board lighting, no text overlays.`;
};

export const buildCaseBackgroundImagePrompt = ({
  title = "",
  sections = [],
}: {
  title?: string;
  sections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
}) => {
  const subjectTitle = normalizeLine(title) || "Cold Case Agency case background";
  const context = (Array.isArray(sections) ? sections : [])
    .slice(0, 4)
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Section ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""))
        .replace(/^([^:]+:\s*)/, "")
        .slice(0, 180);
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("; ");

  return `Create a cinematic 16:9 investigative key art image for the Cold Case Agency case background "${subjectTitle}". ${
    context ? `Story context: ${context}. ` : ""
  }Show an atmospheric, visually striking establishing scene that feels grounded, premium, and mysterious without revealing the culprit, the final twist, or any solution-only clue. Emphasize moody lighting, strong composition, subtle environmental storytelling, believable urban detail, restrained tension, and no text overlays.`;
};

export const buildVictimProfileDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentFields = {},
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentFields?: Record<string, unknown>;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const fieldOrder = [
    "name",
    "age",
    "sex",
    "hair",
    "eyes",
    "height",
    "build",
    "marital_status",
    "occupation",
    "employer",
    "social_context",
    "known_relationships",
    "image_prompt",
  ];

  const currentProfile = fieldOrder
    .map((field) => {
      const value = normalizeLine(currentFields?.[field]);
      return value ? `${field}: ${value}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const missingFields = fieldOrder.filter((field) => !normalizeLine(currentFields?.[field]));
  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting a victim profile for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target victim profile evidence: ${normalizeLine(evidenceTitle) || "Victim profile"}.`,
    missingFields.length
      ? `Fill only the missing victim profile fields: ${missingFields.join(", ")}. Preserve any existing authored fields.`
      : "Review and improve the victim profile fields, but keep existing strong facts unless the case context clearly contradicts them.",
    "Keep victim-only identity, background, social context, and relationship information inside the victim profile.",
    "Do not move suspect-only alibi, opportunity, or accusation analysis into the victim profile.",
    "Use only what can be reasonably inferred from the case context. Do not invent unsupported family details or biography.",
    "Return valid JSON only with these keys: name, age, sex, hair, eyes, height, build, marital_status, occupation, employer, social_context, known_relationships, image_prompt.",
    "Each value should be concise, investigative, and dossier-ready. Use plain strings, not arrays.",
    "image_prompt must describe a photorealistic victim dossier portrait consistent with the victim profile details.",
    currentProfile ? `Existing victim profile:\n${currentProfile}` : "Existing victim profile: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildWitnessStatementsDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentSections = [],
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentSections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const currentStatements = (Array.isArray(currentSections) ? currentSections : [])
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Statement ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting witness statement evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Witness statements"}.`,
    'Return valid JSON only with this shape: {"statements":[{"heading":"...","statement":"...","date":"optional"}]}',
    "Each item is one witness statement. Keep these as actual witness statements, not suspect profiles, suspect analysis, alibis, or dossier summaries.",
    'Every `statement` value must be written as a direct quoted statement with visible double quotes, for example `"I left just after midnight."`',
    "Use the heading to identify the witness or statement source cleanly.",
    "Optional `date` may be included when it helps authenticity.",
    "Keep the voice grounded, observational, and interview-ready.",
    "Do not copy suspect profile fields such as age, build, occupation, alibi, opportunity, or behavior notes into these statements.",
    currentStatements ? `Existing witness statements:\n${currentStatements}` : "Existing witness statements: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildTimelineDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentSections = [],
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentSections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const currentTimeline = (Array.isArray(currentSections) ? currentSections : [])
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Timeline Entry ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting timeline evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Timeline"}.`,
    'Return valid JSON only with this shape: {"sections":[{"heading":"...","content_markdown":"..."}]}',
    "Organize the timeline as a sequence of distinct chronological entries.",
    "Each heading should be a time marker or event marker, for example `10:45 PM`, `11:20 PM`, `Next Morning`, or `Two Days Later`.",
    "Each content_markdown value should concisely explain what happened, who was present, what is uncertain, and any contradiction or investigative relevance.",
    "The timeline should help detectives reconstruct the case, not solve it outright.",
    "Do not turn the timeline into suspect profiles, witness dossier summaries, or police report tables.",
    currentTimeline ? `Existing timeline:\n${currentTimeline}` : "Existing timeline: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildSuspectTextsDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentSections = [],
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentSections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const currentThread = (Array.isArray(currentSections) ? currentSections : [])
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Section ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting suspect text-message evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Suspect texts"}.`,
    "Return valid JSON only with this shape: {\"chains\":[{\"heading\":\"...\",\"messages\":[{\"name\":\"...\",\"message\":\"...\",\"timestamp\":\"optional\"}]}]}",
    "Each chain is one recovered conversation thread or group chat slice.",
    "Within each chain, every message must include `name` and `message`, with optional `timestamp` when it helps authenticity.",
    "Write natural short exchanges with subtext, evasion, and pressure where appropriate.",
    'Use `"quoted text"` only when a speaker is explicitly quoting another person or message.',
    "Do not include direct accusation shortcuts or explicit killer confirmation unless the existing case context already supports it.",
    currentThread ? `Existing suspect text thread:\n${currentThread}` : "Existing suspect text thread: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildSuspectEmailsDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentSections = [],
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentSections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const currentEmails = (Array.isArray(currentSections) ? currentSections : [])
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Section ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting suspect email evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Suspect emails"}.`,
    'Return valid JSON only with this shape: {"emails":[{"heading":"...","from":"...","to":"...","cc":"optional","date":"...","subject":"...","body":"..."}]}',
    "Each email object is one recovered email unit or thread entry.",
    "Every email must include heading, from, to, date, subject, and body. Include cc only when it materially helps the evidence.",
    "Write concise, realistic email language with subtext, avoidance, procedural language, or professional pressure where appropriate.",
    'Use `"quoted text"` only when the email explicitly quotes another person or message.',
    "Do not include direct accusation shortcuts or explicit killer confirmation unless the existing case context already supports it.",
    currentEmails ? `Existing suspect emails:\n${currentEmails}` : "Existing suspect emails: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildNewspaperArticlesDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentSections = [],
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentSections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const currentArticles = (Array.isArray(currentSections) ? currentSections : [])
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Section ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting newspaper article evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Newspaper articles"}.`,
    'Return valid JSON only with this shape: {"articles":[{"heading":"...","headline":"...","byline":"...","publication":"...","date":"...","article":"..."}]}',
    "Each article object is one recovered press clipping or article unit.",
    "Every article must include heading, headline, byline, publication, date, and article.",
    "Write in credible public-reporting voice, with realistic local-news framing, not omniscient narration.",
    'Use `"quoted text"` only for direct quoted statements inside the reporting.',
    "Do not include direct accusation shortcuts or explicit killer confirmation unless the existing case context already supports it.",
    currentArticles ? `Existing article evidence:\n${currentArticles}` : "Existing article evidence: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildPoliceReportDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentSections = [],
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentSections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const currentReport = (Array.isArray(currentSections) ? currentSections : [])
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Section ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting police report evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Police report"}.`,
    'Return valid JSON only with this shape: {"report_fields":{"report_number":"...","incident":"...","victim":"...","date_time_reported":"...","reporting_officer":"...","incident_window":"...","body_discovery":"...","location":"...","persons_involved":"...","scene_observations":"...","actions_taken":"..."},"narrative_sections":[{"heading":"...","content_markdown":"..."}]}',
    "Use report_fields for the labeled procedural rows that appear at the top of the report.",
    "Use narrative_sections only for short follow-on report sections that add investigative context without duplicating the top structured fields.",
    "Keep the language factual, procedural, and police-report credible rather than dramatic.",
    'Use `"quoted text"` only for exact quoted statements recorded in the report.',
    "Do not include direct accusation shortcuts or explicit killer confirmation unless the existing case context already supports it.",
    currentReport ? `Existing police report:\n${currentReport}` : "Existing police report: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildCaseBackgroundDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentSections = [],
  currentImagePrompt = "",
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentSections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
  currentImagePrompt?: string;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const currentBackground = (Array.isArray(currentSections) ? currentSections : [])
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Section ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting case background evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Case background"}.`,
    'Return valid JSON only with this shape: {"sections":[{"heading":"...","content_markdown":"..."}],"image_prompt":"..."}',
    "Use 4 to 6 substantial sections when helpful, such as Case Overview, Victim Context, Key Relationships, Known Tensions, Timeline Pressure, and Why The Case Matters Now.",
    "This is the opening narrative artifact, so it should be rich, readable, and investigative rather than generic filler.",
    "Keep the prose coherent across sections and consistent with the rest of the case evidence.",
    "image_prompt must be a ready-to-copy image-generation prompt for ChatGPT or another image model, written as plain prompt text rather than notes about the prompt.",
    "The image prompt must describe a cinematic 16:9 background image for the opening case file.",
    "Make the image visually appealing, atmospheric, and intriguing without spoiling the story or revealing the culprit.",
    "The image prompt should emphasize moody cinematic composition, believable setting detail, restrained mystery, lens/framing cues, and no text overlays rather than explicit violence or solution clues.",
    'Use `"quoted text"` only for exact quoted lines or remembered speech inside the background narrative.',
    "Do not include direct accusation shortcuts or explicit killer confirmation unless the existing case context already supports it.",
    currentBackground ? `Existing case background:\n${currentBackground}` : "Existing case background: none authored yet.",
    normalizeLine(currentImagePrompt) ? `Existing image prompt:\n${normalizeLine(currentImagePrompt)}` : "",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildAdditionalEvidenceDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentSections = [],
  currentImagePrompt = "",
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentSections?: Array<{ heading?: string; content_markdown?: string; content?: string }>;
  currentImagePrompt?: string;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const currentArtifact = (Array.isArray(currentSections) ? currentSections : [])
    .map((section, index) => {
      const heading = normalizeLine(String(section?.heading || "")) || `Section ${index + 1}`;
      const body = normalizeLine(String(section?.content_markdown || section?.content || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(String(section?.content_markdown || section?.content || ""));
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting additional evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Additional evidence"}.`,
    'Return valid JSON only with this shape: {"sections":[{"heading":"...","content_markdown":"..."}],"image_prompt":"..."}',
    "Use this for image-first or artifact-style evidence that does not fit the other fixed evidence formats.",
    "The sections should describe the artifact clearly enough to read on screen and support the investigation.",
    "image_prompt must describe the visual look of the additional evidence artifact so it can serve as a strong artifact image prompt later if needed.",
    "Make the image prompt specific about medium, age, wear, lighting, camera angle, and any important forensic or investigative details.",
    'Use `"quoted text"` only for exact quoted lines appearing inside the artifact text.',
    "Do not include direct accusation shortcuts or explicit killer confirmation unless the existing case context already supports it.",
    currentArtifact ? `Existing additional evidence:\n${currentArtifact}` : "Existing additional evidence: none authored yet.",
    normalizeLine(currentImagePrompt) ? `Existing image prompt:\n${normalizeLine(currentImagePrompt)}` : "",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildSuspectImagePrompt = (suspect: Record<string, unknown> = {}) => {
  const name = normalizeLine(String(suspect?.name || "")) || "Unknown suspect";
  const role = normalizeLine(String(suspect?.role || suspect?.relation || "")) || "person of interest";
  const descriptors = formatSubjectDetails([
    ["Age", suspect?.age],
    ["Sex", suspect?.sex],
    ["Hair", suspect?.hair],
    ["Eyes", suspect?.eyes],
    ["Height", suspect?.height],
    ["Build", suspect?.build],
    ["Marital status", suspect?.marital_status],
    ["Occupation", suspect?.occupation],
    ["Employer", suspect?.employer],
    ["Motive", suspect?.motive],
  ]);

  return `Photorealistic suspect dossier portrait of ${name}, ${role}. Subject details: ${descriptors || "Use the stored suspect profile."} Documentary realism, neutral background, subtle investigative lighting, no text overlays.`;
};

export const buildSuspectEvidencePrompt = ({
  name = "",
  role = "",
}: {
  name?: string;
  role?: string;
}) => {
  const normalizedName = normalizeLine(name) || "Unknown suspect";
  const normalizedRole = normalizeLine(role) || "person of interest";

  return [
    `Write one suspect evidence dossier for ${normalizedName}, ${normalizedRole}.`,
    "Keep this suspect-focused. Do not place victim relationship notes or victim-only family context here unless it directly explains the suspect's motive, alibi, or opportunity.",
    "Source and update the suspect profile description fields when supported by the case: Age, Sex, Hair, Eyes, Height, Build, Marital Status, Occupation, Employer, and a portrait-ready image prompt.",
    "Source and write five suspect evidence fields when supported by the case: Alibi, Evidence Against, Evidence In Favor, Opportunity, and Behavior Notes.",
    'Use `**LABEL:** value` when writing structured suspect evidence blocks.',
    'Use `"quoted text"` only for direct statements, interrogative language, or exact recalled speech.',
    "Evidence Against should summarize concrete facts, contradictions, forensic links, or timeline pressure.",
    "Evidence In Favor should summarize verified facts that reduce suspicion or support the suspect's account.",
    "Behavior Notes should stay observational and investigative, not melodramatic.",
  ].join("\n");
};

export const buildSuspectEvidenceDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  suspect = {},
  witnessMentions = [],
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  suspect?: Record<string, unknown>;
  witnessMentions?: Array<{ heading?: string; body?: string }>;
  evidenceFiles?: Array<{ title?: string; type?: string; sections?: Array<{ heading?: string; content_markdown?: string; content?: string }> }>;
}) => {
  const suspectName = normalizeLine(String(suspect?.name || "")) || "Unknown suspect";
  const suspectRole = normalizeLine(String(suspect?.relation || suspect?.role || "")) || "person of interest";
  const currentEvidence = formatSubjectDetails([
    ["Age", suspect?.age],
    ["Sex", suspect?.sex],
    ["Hair", suspect?.hair],
    ["Eyes", suspect?.eyes],
    ["Height", suspect?.height],
    ["Build", suspect?.build],
    ["Marital Status", suspect?.marital_status],
    ["Occupation", suspect?.occupation],
    ["Employer", suspect?.employer],
    ["Motive", suspect?.motive],
    ["Alibi", suspect?.alibi],
    ["Evidence Against", suspect?.evidence_against],
    ["Evidence In Favor", suspect?.evidence_in_favor],
    ["Opportunity", suspect?.opportunity],
    ["Behavior Notes", suspect?.behavior_notes],
    ["Image Prompt", suspect?.image_prompt],
  ]);
  const missingFields = [
    ["age", suspect?.age],
    ["sex", suspect?.sex],
    ["hair", suspect?.hair],
    ["eyes", suspect?.eyes],
    ["height", suspect?.height],
    ["build", suspect?.build],
    ["marital_status", suspect?.marital_status],
    ["occupation", suspect?.occupation],
    ["employer", suspect?.employer],
    ["alibi", suspect?.alibi],
    ["evidence_against", suspect?.evidence_against],
    ["evidence_in_favor", suspect?.evidence_in_favor],
    ["opportunity", suspect?.opportunity],
    ["behavior_notes", suspect?.behavior_notes],
    ["image_prompt", suspect?.image_prompt],
  ]
    .filter(([, value]) => !normalizeLine(String(value || "")))
    .map(([label]) => label);

  const witnessContext = witnessMentions
    .map((item, index) => {
      const heading = normalizeLine(String(item?.heading || "")) || `Witness Section ${index + 1}`;
      const body = normalizeLine(String(item?.body || ""));
      return body ? `${heading}: ${body}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const evidenceContext = evidenceFiles
    .map((file, fileIndex) => {
      const title = normalizeLine(String(file?.title || "")) || `Evidence ${fileIndex + 1}`;
      const type = normalizeLine(String(file?.type || "")) || "evidence";
      const body = (Array.isArray(file?.sections) ? file.sections : [])
        .map((section, sectionIndex) => {
          const heading = normalizeLine(String(section?.heading || "")) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(
            String(section?.content_markdown || section?.content || "")
          );
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${body ? ` -> ${body}` : ""}`;
    })
    .filter(Boolean)
    .join("\n");

  return [
    `You are drafting suspect evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target suspect: ${suspectName} (${suspectRole}).`,
    missingFields.length
      ? `Fill only the missing fields: ${missingFields.join(", ")}. Preserve any existing authored fields.`
      : "Review and improve the suspect profile and evidence fields, but keep existing strong facts unless the case context clearly contradicts them.",
    "Use only what can be reasonably inferred from the case context. Do not invent victim-family relationship details unless they directly explain this suspect's motive, alibi, or opportunity.",
    "Return valid JSON only with these keys: age, sex, hair, eyes, height, build, marital_status, occupation, employer, alibi, evidence_against, evidence_in_favor, opportunity, behavior_notes, image_prompt.",
    "Each value should be concise, investigative, and case-specific. Use plain strings, not arrays.",
    "image_prompt must describe a photorealistic suspect dossier portrait consistent with the suspect profile details.",
    currentEvidence ? `Existing suspect profile and evidence:\n${currentEvidence}` : "Existing suspect profile and evidence: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    witnessContext ? `Witness mentions relevant to this suspect:\n${witnessContext}` : "Witness mentions relevant to this suspect: none provided.",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildAutopsyEvidenceDraftPrompt = ({
  caseTitle = "",
  caseSummary = "",
  briefingBody = "",
  evidenceTitle = "",
  currentFields = {},
  evidenceFiles = [],
}: {
  caseTitle?: string;
  caseSummary?: string;
  briefingBody?: string;
  evidenceTitle?: string;
  currentFields?: Record<string, unknown>;
  evidenceFiles?: any[];
}) => {
  const fieldOrder = [
    "caseNumber",
    "name",
    "age",
    "sex",
    "examiner",
    "estimatedTimeOfDeath",
    "causeOfDeath",
    "mannerOfDeath",
    "height",
    "weight",
    "identifyingMarks",
    "externalFindings",
    "internalFindings",
    "toxicology",
    "evidenceRecovered",
    "examinerOpinion",
  ];

  const currentEvidence = fieldOrder
    .map((field) => {
      const value = normalizeLine(currentFields?.[field]);
      return value ? `${field}: ${value}` : "";
    })
    .filter(Boolean)
    .join("\n");

  const missingFields = fieldOrder.filter((field) => !normalizeLine(currentFields?.[field]));
  const evidenceContext = (Array.isArray(evidenceFiles) ? evidenceFiles : [])
    .slice(0, 8)
    .map((file, index) => {
      const title = normalizeLine(file?.title) || `Evidence ${index + 1}`;
      const type = normalizeLine(file?.type) || "evidence";
      const sectionSummary = (Array.isArray(file?.sections) ? file.sections : [])
        .slice(0, 3)
        .map((section: any, sectionIndex: number) => {
          const heading = normalizeLine(section?.heading) || `Section ${sectionIndex + 1}`;
          const content = normalizeLine(section?.content_markdown || section?.content).slice(0, 320);
          return content ? `${heading}: ${content}` : "";
        })
        .filter(Boolean)
        .join(" | ");
      return `${title} (${type})${sectionSummary ? ` -> ${sectionSummary}` : ""}`;
    })
    .join("\n");

  return [
    `You are drafting autopsy evidence for the Cold Case Agency case "${normalizeLine(caseTitle) || "Untitled case"}".`,
    `Target evidence file: ${normalizeLine(evidenceTitle) || "Autopsy report"}.`,
    missingFields.length
      ? `Fill only the missing autopsy fields: ${missingFields.join(", ")}. Preserve any existing authored fields unless the current value is obviously incomplete.`
      : "Review and improve the autopsy fields while preserving established case facts and report consistency.",
    "Use only what can be reasonably inferred from the case context. Do not invent forensic certainty that the case record does not support.",
    "Return valid JSON only with these keys: caseNumber, name, age, sex, examiner, estimatedTimeOfDeath, causeOfDeath, mannerOfDeath, height, weight, identifyingMarks, externalFindings, internalFindings, toxicology, evidenceRecovered, examinerOpinion.",
    "Each value should be concise, investigative, and appropriate for a medical examiner report. Use plain strings, not arrays or nested objects.",
    currentEvidence ? `Existing autopsy fields:\n${currentEvidence}` : "Existing autopsy fields: none authored yet.",
    caseSummary ? `Case summary:\n${normalizeLine(caseSummary)}` : "",
    briefingBody ? `Case briefing:\n${normalizeLine(briefingBody)}` : "",
    evidenceContext ? `Case evidence context:\n${evidenceContext}` : "Case evidence context: none provided.",
  ]
    .filter(Boolean)
    .join("\n\n");
};

export const buildAutopsyReportImagePrompt = ({
  title = "",
  sections = [],
}: {
  title?: string;
  sections?: any[];
}) => {
  const rows = extractProfileRowsFromSections(sections);
  const subjectTitle = normalizeLine(title) || "Unknown decedent";
  const descriptors = formatSubjectDetails(
    rows
      .filter((row) =>
        /^(name|age|sex|case\s*#|cause of death|manner of death|examiner|estimated time of death)$/i.test(
          row.label
        )
      )
      .map((row) => [row.label, row.value])
  );

  return `Photorealistic forensic autopsy report support image for ${subjectTitle}. Subject details: ${descriptors || "Use the stored report details."} Clinical documentary realism, restrained forensic lighting, medical examiner workspace, no gore, no text overlays, no stylized horror treatment.`;
};

export const buildEvidenceStructurePrompt = ({
  evidenceType = "",
  title = "",
}: {
  evidenceType?: string;
  title?: string;
}) => {
  const normalizedType = normalizeLine(evidenceType).toLowerCase();
  const normalizedTitle = normalizeLine(title) || "Untitled evidence file";
  const baseRules = [
    `Write one ${normalizedType.replaceAll("_", " ") || "evidence"} file for "${normalizedTitle}".`,
    "Stay concise, investigative, and production-ready.",
    'Use `**LABEL:** value` for structured report fields and metadata rows.',
    'Use `"quoted text"` only for direct quotations, exact statements, or spoken lines.',
    "Keep section headings short, factual, and uppercase-safe.",
    "Do not add markdown lists unless the evidence type clearly requires them.",
  ];

  const typeSpecific =
    normalizedType === "autopsy_report"
      ? [
          "Start with structured autopsy fields for Case #, Name, Age, Sex, Examiner, Estimated Time of Death, Cause of Death, Manner of Death, Height, Weight, and Identifying Marks.",
          "Follow with clear findings fields such as External Findings, Internal Findings, Toxicology, Evidence Recovered, and Examiner Opinion.",
          "If additional narrative is needed, place it in short report sections after the structured fields.",
        ]
      : normalizedType === "police_report"
        ? [
            "Open with explicit labeled report identifiers such as Report Number, Incident, Victim, Date/Time Reported, Reporting Officer, and Incident Window.",
            "Use short procedural follow-on sections only when they add scene context, observations, or actions taken.",
            "Keep the tone factual and report-like rather than dramatic.",
          ]
        : normalizedType === "case_background"
          ? [
              "Use several substantial narrative sections that introduce the victim, tensions, stakes, and why the case matters now.",
              "Treat this as the opening case-file artifact, not a thin summary or bullet list.",
              "Keep the voice investigative, readable, and coherent across sections.",
            ]
          : normalizedType === "additional_evidence"
            ? [
                "Treat this as a flexible artifact file that may support an image-first clue, recovered document, receipt, note, map, or other supplemental evidence.",
                "Keep the written sections clear and artifact-specific rather than generic narrative.",
                "Include or preserve a strong artifact image prompt when visual generation is useful.",
              ]
        : normalizedType === "victim_profile"
          ? [
              "Keep victim-only identity, background, known relationships, and family context inside the victim profile.",
              "Source and update victim profile descriptors such as name, age, sex, hair, eyes, height, build, marital status, occupation, employer, social context, known relationships, and a portrait-ready image prompt.",
              "Do not place suspect alibis, evidence against a suspect, or opportunity analysis in the victim profile.",
            ]
          : normalizedType === "suspect_profile"
            ? [
                "Keep suspect profile descriptors current, including age, sex, hair, eyes, height, build, marital status, occupation, employer, and a portrait-ready image prompt.",
                "Source and write suspect-only evidence such as alibi, evidence against, evidence in favor, opportunity, and behavioral observations.",
                "Do not move victim-only known relationships or family background into suspect evidence fields.",
              ]
        : normalizedType === "suspect_texts"
          ? [
              "Each section should represent one recovered text-message chain.",
              'Format each message as `Sender: message` with an optional timestamp prefix when useful.',
              "Keep the exchanges short, natural, and easy to parse into chain-based message bubbles.",
              'Use `"quoted text"` only when a speaker is explicitly quoting someone else.',
            ]
          : normalizedType === "suspect_emails"
            ? [
                "Each section should represent one recovered email unit.",
                "Include realistic email headers such as From, To, Cc when relevant, Date, and Subject.",
                "Keep the body natural, specific, and case-relevant, with clear sender/recipient context.",
              ]
            : normalizedType === "newspaper_articles"
              ? [
                  "Each section should represent one recovered article unit or clipping.",
                  "Include Headline, Byline, Publication, Date, and Article fields in a consistent article structure.",
                  "Keep the writing in realistic public-reporting voice rather than omniscient case-summary voice.",
                ]
            : [
                "Lead with the most useful structured facts first, then any supporting narrative.",
                "Preserve a consistent internal case-file tone across every section.",
              ];

  return [...baseRules, ...typeSpecific].join("\n");
};
